import AgoraRTC from 'agora-rtc-sdk';
import {AgoraEventName, AppKey} from "../utis/customEnum";
import {Logger} from "../utis/Log";
import {isDebug} from "../appConfig";

export const VideoCodec = {
	vp8: "vp8",
	vp9: "vp9",
	h264: "h264"
}

export const RTCInitErrorName = {
	// BAD_ENVIRONMENT: 不支持当前使用的浏览器
	badEnvironment: 'BAD_ENVIRONMENT',
}

export const RTCJoinChannelErrorName = {
	// INVALID_OPERATION: 当前无法加入频道，通常是因为重复调用了 Client.join
	invalidOperation: 'INVALID_OPERATION',
	// UID_CONFLICT: 当前 Client 的 uid 和频道中其他 Client 的 uid 冲突
	uidConflict: 'UID_CONFLICT',
	// ERR_REPEAT_JOIN: 当前 Client 已经加入了频道
	repeatJoin: 'ERR_REPEAT_JOIN',
	// SOCKET_ERROR: 加入频道过程中和 Agora 服务器断开连接
	socketError: 'SOCKET_ERROR',
	// CANNOT_MEET_AREA_DEMAND: 由于用户在指定的访问区域外，导致无法建立连接
	areaDemandNotMeet: 'CANNOT_MEET_AREA_DEMAND',
}

export const RTCStreamInitErrorName = {
	// NotAllowedError: 用户拒绝授予对应的摄像头或麦克风权限
	notAllowed: 'NotAllowedError',
	// MEDIA_OPTION_INVALID: 摄像头被占用或者分辨率不支持（早期浏览器）
	invalidMediaOption: 'MEDIA_OPTION_INVALID',
	// DEVICES_NOT_FOUND: 没有找到设备
	deviceNotFound: 'DEVICES_NOT_FOUND',
	// NOT_SUPPORTED: 浏览器不支持获取获取摄像头和麦克风
	notSupported: 'NOT_SUPPORTED',
	// PERMISSION_DENIED: 浏览器禁用设备或者用户拒绝打开设备
	permissionDenied: 'PERMISSION_DENIED',
	// CONSTRAINT_NOT_SATISFIED: 配置参数不合法（早期浏览器）
	constraintNotSatisfied: 'CONSTRAINT_NOT_SATISFIED',
	// PluginNotInstalledProperly: 用户尝试在 Chrome 上进行屏幕共享，但未安装屏幕共享插件，或使用了错误的 extensionId
	pluginNotInstalled: 'PluginNotInstalledProperly',
	// UNDEFINED: 未定义错误
	undefined: 'UNDEFINED',
}

export const RTCPublishErrorName = {
	// STREAM_ALREADY_PUBLISHED: 该 stream 已经发布
	streamPublished: 'STREAM_ALREADY_PUBLISHED',
	// INVALID_LOCAL_STREAM: 传入的 stream 格式非法
	invalidLocalStream: 'INVALID_LOCAL_STREAM',
	// INVALID_OPERATION": 当前不在频道中，可能是没有加入频道或者是网络波动导致暂时断开连接
	invalidOperation: 'INVALID_OPERATION',
	// PUBLISH_STREAM_FAILED": 发布失败，一般是在发布过程中连接丢失
	publishFailed: 'PUBLISH_STREAM_FAILED',
	// PEERCONNECTION_FAILED": 媒体传输通道建立失败
	peerConnectionFailed: 'PEERCONNECTION_FAILED',
	// REQUEST_ABORT": 发布过程中连接断开，退出频道。建议监听 rejoin 事件，等重连成功后再次调用 publish
	requestAbort: 'REQUEST_ABORT',
}

const getRTCPublishError = (str) => {
	if (str === RTCPublishErrorName.streamPublished) {
		return {
			name: RTCPublishErrorName.streamPublished,
			message: "The stream has been published",
		}
	} else if (str === RTCPublishErrorName.invalidLocalStream) {
		return {
			name: RTCPublishErrorName.invalidLocalStream,
			message: "Invalid local stream",
		}
	} else if (str === RTCPublishErrorName.invalidOperation) {
		return {
			name: RTCPublishErrorName.invalidOperation,
			message: "Not currently in the channel, may be temporarily disconnected due to not joining the channel or network fluctuations",
		}
	} else if (str === RTCPublishErrorName.publishFailed) {
		return {
			name: RTCPublishErrorName.publishFailed,
			message: "Failed to publish, usually the connection is lost during the publishing process",
		}
	} else if (str === RTCPublishErrorName.peerConnectionFailed) {
		return {
			name: RTCPublishErrorName.peerConnectionFailed,
			message: "Media transmission channel establishment failed",
		}
	} else if (str === RTCPublishErrorName.requestAbort) {
		return {
			name: RTCPublishErrorName.requestAbort,
			message: "Lost connection during publishing, log out of the channel",
		}
	}
	return undefined
}

const getRTCInitError = (str) => {
	if (str === RTCInitErrorName.badEnvironment) {
		return {
			name: RTCInitErrorName.badEnvironment,
			message: "The current browser is not supported",
		}
	}
	return undefined
}

const getRTCJoinChannelError = (str) => {
	if (str === RTCJoinChannelErrorName.invalidOperation) {
		return {
			name: RTCJoinChannelErrorName.invalidOperation,
			message: "Can't join channel right now",
		}
	} else if (str === RTCJoinChannelErrorName.uidConflict) {
		return {
			name: RTCJoinChannelErrorName.uidConflict,
			message: "The uid of the current client conflicts with the uid of other clients in the channel",
		}
	} else if (str === RTCJoinChannelErrorName.repeatJoin) {
		return {
			name: RTCJoinChannelErrorName.repeatJoin,
			message: "The current client has joined the channel",
		}
	} else if (str === RTCJoinChannelErrorName.socketError) {
		return {
			name: RTCJoinChannelErrorName.socketError,
			message: "Disconnect from server while joining channel",
		}
	} else if (str === RTCJoinChannelErrorName.areaDemandNotMeet) {
		return {
			name: RTCJoinChannelErrorName.areaDemandNotMeet,
			message: "The connection could not be established because the user is outside the specified access area",
		}
	}
	return undefined
}

export default class RTCClient{

	/**
	 * @Desc
	 * @Params
	 * @Return true: 支持当前浏览器；false不支持当前浏览器
	*/
	static checkSystemRequirements() {
		return AgoraRTC.checkSystemRequirements()
	}

	constructor(channelName, codec, eventCallback) {
		this.config = {
		  mode: 'rtc',
		  codec: codec || VideoCodec.vp8,
		  appID: AppKey.AgoraAppKey,
		  channelName: channelName,
		  uid: 0,
		  token: null,
		}
		this.client = null
		this.eventCallback = eventCallback
		this.localStream = null
		this.remoteStream = null

		Logger.print("rtc sdk version: " + AgoraRTC.VERSION)
	}

	subscribeClientEvents() {
		// local published stream
		this.client.on(AgoraEventName.StreamPublished, (event) => {
			this.localStream = event.stream
			this.eventCallback && this.eventCallback(AgoraEventName.StreamPublished)
		});
		// remote published stream
		this.client.on(AgoraEventName.StreamAdded, (event) => {
			Logger.print("subscribe remote stream: ")
			this.client.subscribe(event.stream, null, (err) => {
				Logger.print("subscribe remote stream fail: ", err)
			})
		});
		// subscribed remote stream
		this.client.on(AgoraEventName.StreamSubscribed, (event) => {
			this.remoteStream = event.stream
			this.eventCallback && this.eventCallback(AgoraEventName.StreamSubscribed)
		});
		this.client.on(AgoraEventName.PeerLeave, (event) => {
			this.eventCallback && this.eventCallback(AgoraEventName.PeerLeave)
		});
		this.client.on(AgoraEventName.muteVideo, (event) => {
			this.eventCallback && this.eventCallback(AgoraEventName.muteVideo)
		});
		this.client.on(AgoraEventName.unmuteVideo, (event) => {
			this.eventCallback && this.eventCallback(AgoraEventName.unmuteVideo)
		});
	}

	joinChannel(onFail) {
		if (!this.config.channelName.length) {
			Logger.print(`Invalid channel name: ${this.config.channelName}`)
			return;
		}

		const client = AgoraRTC.createClient({ mode: this.config.mode, codec: this.config.codec })
		this.setLogLevel()

		this.client = client
		this.subscribeClientEvents()

		client.init(this.config.appID, () => {
			client.join(this.config.token, this.config.channelName, this.config.uid, undefined, (uid) => {
				const stream = AgoraRTC.createStream({
					streamID: this.config.uid,
					video: true,
					audio: true,
					screen: false
				});

				stream.init(() => {
					client.publish(stream, (error) => {
						Logger.print(`publish local stream error: ${error}`)
						onFail && onFail(getRTCPublishError(error))
					})
				}, ({type, msg, info}) => {
					Logger.print(`init local stream error: ${msg} - ${info || ""}`)
					onFail && onFail({name: msg, message: info || ""})
				})
			}, (error) => {
				Logger.print(`join channel error: ${error}`)
				onFail && onFail(getRTCJoinChannelError(error))
			})
		}, (error) => {
			Logger.print(`init client error: ${error}`)
			onFail && onFail(getRTCInitError(error))
		})
	}

	leaveChannel(callback) {
		if (this.client) {
			if (this.localStream) {
				this.client.unpublish(this.localStream)
				this.localStream.close() // 关闭webcam
			}

			this.client.leave(() => {
				this.client = null
				this.localStream = null
				this.remoteStream = null

				callback && callback()
			})
		}
	}

	setLogLevel() {
		if (!isDebug) {
			AgoraRTC.Logger.setLogLevel(AgoraRTC.Logger.NONE)
		}
	}
}

